import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Layers, MoreHorizontal } from 'lucide-react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb } from '@/features/media';

import type { GalleryItem } from './product-media-gallery';

export interface ProductMediaTileProps {
  item: GalleryItem;
  position: number;
  count: number;
  selected: boolean;
  /** Some file is selected: every checkbox shows, not only on hover. */
  selecting: boolean;
  canEdit: boolean;
  onToggleSelect: (id: string, range: boolean) => void;
  onOpen: (item: GalleryItem) => void;
  onMakeFeatured: (id: string) => void;
  onVariants: (id: string) => void;
  onRemove: (id: string) => void;
}

function TileBadges({ item, featured }: { item: GalleryItem; featured: boolean }) {
  const { t } = useTranslation('products');
  const scoped = item.variantIds.length;
  return (
    <div className="pointer-events-none absolute inset-x-2 bottom-2 flex flex-wrap items-end gap-1">
      {featured ? (
        <span className="bg-background/90 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold">
          {t('media.featured')}
        </span>
      ) : null}
      {item.media.alt ? null : (
        <span className="bg-warning text-warning-foreground rounded-full px-1.5 py-0.5 text-[10px]">
          {t('media.noAltShort')}
        </span>
      )}
      {scoped > 0 ? (
        <span className="bg-background/90 ml-auto inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] tabular-nums">
          <Layers className="size-3" aria-hidden />
          {t('media.scopedCount', { count: scoped })}
        </span>
      ) : null}
    </div>
  );
}

/** A gallery file on a store's product page: only the picture, the gallery is edited on master. */
export function ProductMediaStaticTile({
  item,
  position,
}: {
  item: GalleryItem;
  position: number;
}) {
  const featured = position === 1;
  return (
    <li
      className={cn('relative', featured && 'col-span-2 row-span-2')}
      data-testid="product-media-tile"
    >
      <MediaThumb
        media={item.media}
        rendition={featured ? 'medium' : 'thumb'}
        className="size-full rounded-lg border"
      />
      <TileBadges item={item} featured={featured} />
    </li>
  );
}

/**
 * One gallery file. The tile itself is the drag handle and opens the details on click; the
 * checkbox and the menu sit above it so neither starts a drag.
 */
export function ProductMediaTile({
  item,
  position,
  count,
  selected,
  selecting,
  canEdit,
  onToggleSelect,
  onOpen,
  onMakeFeatured,
  onVariants,
  onRemove,
}: ProductMediaTileProps) {
  const { t } = useTranslation('products');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.mediaId,
    disabled: !canEdit,
  });
  const featured = position === 1;
  const name = item.media.filename ?? item.media.alt ?? t('media.untitled');

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'group relative',
        featured && 'col-span-2 row-span-2',
        isDragging && 'z-10 opacity-40',
      )}
      data-testid="product-media-tile"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={t('media.tileLabel', { name, position, count })}
        onClick={() => onOpen(item)}
        className={cn(
          'focus-visible:ring-ring/50 block size-full rounded-lg outline-none focus-visible:ring-[3px]',
          canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        )}
      >
        <MediaThumb
          media={item.media}
          rendition={featured ? 'medium' : 'thumb'}
          className={cn(
            'size-full rounded-lg border transition',
            'group-hover:brightness-95',
            selected && 'ring-primary ring-2 ring-offset-1',
          )}
        />
      </button>

      {canEdit ? (
        <>
          <div
            className={cn(
              'absolute top-2 left-2 transition-opacity',
              selected || selecting
                ? 'opacity-100'
                : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
            )}
          >
            <Checkbox
              checked={selected}
              aria-label={t('media.selectFile', { name })}
              className="bg-background shadow-sm"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onToggleSelect(item.mediaId, e.shiftKey);
              }}
            />
          </div>
          <div className="absolute top-1.5 right-1.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 has-[[data-state=open]]:opacity-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon-xs"
                  className="shadow-sm"
                  aria-label={t('media.actionsFor', { name })}
                >
                  <MoreHorizontal aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-44">
                <DropdownMenuItem onSelect={() => onOpen(item)}>{t('media.edit')}</DropdownMenuItem>
                {featured ? null : (
                  <DropdownMenuItem onSelect={() => onMakeFeatured(item.mediaId)}>
                    {t('media.setFeatured')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => onVariants(item.mediaId)}>
                  {t('media.variantsAction')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => onRemove(item.mediaId)}>
                  {t('media.remove')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      ) : null}

      <TileBadges item={item} featured={featured} />
    </li>
  );
}
